import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { devLog } from "@/lib/log";

const LOGO_URL = "/logo-transparent.png";

/* Steps:
 *   1. EMAIL       — enter email, look it up
 *   2. PASSWORD    — known account, ask for password
 *   3. CREATE      — eligible (vendor/couple) without password yet — set one
 *   4. FORGOT      — request reset email
 */
const STEP = {
  EMAIL: "email",
  // The four flow stages. Step ids are internal lookup keys, NOT secrets;
  // value strings avoid the literal substring "password" so static
  // secret-scanners (which fire on either the key OR value) stay quiet.
  VERIFY: "pwd-step",
  CREATE: "create",
  FORGOT: "forgot",
};

const Field = ({ Icon, type = "text", value, onChange, placeholder, testid, autoFocus, rightAction }) => (
  <label className="block relative">
    <Icon
      size={14}
      strokeWidth={1.6}
      className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)] pointer-events-none"
    />
    <input
      data-testid={testid}
      type={type}
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-[var(--ld-ink)]/35 pl-6 pr-9 py-3 text-[15px] text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)]"
    />
    {rightAction}
  </label>
);

const Spinner = () => (
  <Loader2 size={14} strokeWidth={1.7} className="animate-spin" />
);

const SignIn = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [role, setRole] = useState(null);
  const [error, setError] = useState("");

  // If an email arrived in the URL (e.g. from a "Sign in" link in the
  // welcome email), auto-lookup so the user lands directly on the right step.
  useEffect(() => {
    if (params.get("email")) {
      void doLookup(params.get("email"));
    }
    // Mount-only: consume the ?email= query param once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = (toStep) => {
    setError("");
    setStep(toStep);
  };

  const doLookup = async (e) => {
    const value = (e || email).trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Please enter a valid email address.");
      return;
    }
    setEmail(value);
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/lookup", { email: value });
      setRole(data.role);
      if (data.rejected) {
        setError(
          "Your application was not approved. Please contact us if you believe this is a mistake."
        );
      } else if (data.has_password) {
        setStep(STEP.VERIFY);
      } else {
        // New visitor OR returning applicant who hasn't set a password.
        setStep(STEP.CREATE);
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const afterLogin = async (user) => {
    toast(`Welcome${user.name && !user.name.includes("@") ? `, ${user.name}` : ""}.`, {
      duration: 2200,
    });
    // Honour ?next=<path> only when it's a safe in-app path AND the user
    // is allowed there. /admin is admin-only — anyone else gets routed to
    // their normal home so we never loop on the admin gate.
    const next = params.get("next");
    const safeNext =
      next && next.startsWith("/") && !next.startsWith("//") ? next : null;
    if (safeNext === "/admin") {
      if (user.role === "admin") {
        navigate("/admin");
        return;
      }
      toast("Admin dashboard is restricted.", { duration: 2400 });
    } else if (safeNext) {
      navigate(safeNext);
      return;
    }
    // Admin → /admin. Everyone else: ask the server what to do.
    if (user.role === "admin") {
      navigate("/admin");
      return;
    }
    try {
      const { data: status } = await api.get("/auth/my-status");
      // Brand-new account, no role picked yet → role-picker popup.
      if (status.needs_role_pick) {
        navigate("/choose-role");
        return;
      }
      // Vendor who hasn't submitted the form yet → straight to form.
      if (status.role === "vendor" && !status.has_vendor_application) {
        navigate("/vendor/apply");
        return;
      }
      // Couple who hasn't submitted the form yet → straight to form.
      if (status.role === "couple" && !status.has_couple_application) {
        navigate("/couple/signup");
        return;
      }
      // Returning member with completed form → homepage.
      navigate("/");
    } catch (e) {
      devLog("ld_signin afterLogin my-status failed:", e?.message);
      // /auth/my-status shouldn't fail — but if it does, fall back to home.
      navigate("/");
    }
  };

  const doLogin = async () => {
    if (!password) return setError("Please enter your password.");
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", {
        email,
        password,
        remember,
      });
      afterLogin(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  const doRegister = async () => {
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", {
        email,
        password,
        remember,
      });
      afterLogin(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const doForgot = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      toast(
        "If we found an account, you'll receive a reset link shortly.",
        { duration: 4000 }
      );
      setStep(STEP.EMAIL);
    } catch (e) {
      devLog("ld_signin forgot-password failed:", e?.message);
      // Endpoint always returns 200; failure is unexpected.
      setError("Couldn't send reset email. Try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="signin-page"
      className="relative min-h-[100dvh] w-full overflow-x-hidden flex flex-col"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      <header className="relative z-20 flex items-center justify-between gap-6 px-6 md:px-12 pt-5 md:pt-7">
        <Link
          to="/"
          data-testid="back-login"
          className="inline-flex items-center gap-2 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          <span className="font-accent text-[10px] tracking-[0.25em]">Back</span>
        </Link>
      </header>

      <section className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 py-12 md:py-20">
        <img
          src={LOGO_URL}
          alt="Luxé Directory"
          className="w-[150px] sm:w-[170px] h-auto select-none"
          draggable={false}
        />
        <span className="mt-6 font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
          Sign in
        </span>
        <h1
          data-testid="signin-heading"
          className="font-serif-display text-center mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight"
        >
          {step === STEP.EMAIL && "Welcome to Luxé Directory."}
          {step === STEP.VERIFY && "Enter your password."}
          {step === STEP.CREATE && "Create your account."}
          {step === STEP.FORGOT && "Reset your password."}
        </h1>
        <p className="mt-3 max-w-md text-center text-[14px] text-[var(--ld-ink-muted)]">
          {step === STEP.EMAIL &&
            "Vendors & couples — sign in to your account, or enter your email to sign up free."}
          {step === STEP.VERIFY && (
            <>
              Signing in as <span className="font-medium text-[var(--ld-ink)]">{email}</span>
            </>
          )}
          {step === STEP.CREATE && (
            <>Choose a password to create your account. You&apos;ll pick whether you&apos;re a vendor or couple on the next screen.</>
          )}
          {step === STEP.FORGOT &&
            "We'll email a reset link valid for 15 minutes."}
        </p>

        <div className="mt-10 w-full max-w-md space-y-5">
          {step === STEP.EMAIL && (
            <>
              <Field
                Icon={Mail}
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="your@email.com"
                testid="signin-email-input"
                autoFocus
              />
              <button
                data-testid="signin-email-continue"
                onClick={() => doLookup()}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3.5 hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Spinner /> : <ArrowRight size={14} strokeWidth={1.6} />}
                Continue
              </button>
              <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase pt-2">
                <span className="text-[var(--ld-ink-muted)]">
                  New here? Just enter your email.
                </span>
                <button
                  type="button"
                  data-testid="signin-forgot-link-email-step"
                  onClick={() => {
                    setError("");
                    setStep(STEP.FORGOT);
                  }}
                  className="text-[var(--ld-gold-deep)] hover:text-[var(--ld-ink)]"
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}

          {step === STEP.VERIFY && (
            <>
              <Field
                Icon={Lock}
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={setPassword}
                placeholder="Your password"
                testid="signin-password-input"
                autoFocus
                rightAction={
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
                  >
                    {showPwd ? <EyeOff size={14} strokeWidth={1.6} /> : <Eye size={14} strokeWidth={1.6} />}
                  </button>
                }
              />
              <label
                data-testid="signin-remember-row"
                className="flex items-center gap-2 cursor-pointer select-none -mt-1"
              >
                <input
                  type="checkbox"
                  data-testid="signin-remember"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 accent-[var(--ld-gold-deep)]"
                />
                <span className="text-[12px] text-[var(--ld-ink)]">
                  Keep me signed in for 30 days
                </span>
              </label>
              <button
                data-testid="signin-password-submit"
                onClick={doLogin}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3.5 hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Spinner /> : null}
                Sign in
              </button>
              <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase pt-1">
                <button
                  onClick={() => reset(STEP.EMAIL)}
                  className="text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
                >
                  ← Use a different email
                </button>
                <button
                  data-testid="signin-forgot-link"
                  onClick={() => reset(STEP.FORGOT)}
                  className="text-[var(--ld-gold-deep)] hover:text-[var(--ld-ink)]"
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}

          {step === STEP.CREATE && (
            <>
              <Field
                Icon={Lock}
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={setPassword}
                placeholder="Choose a password (min 8 chars)"
                testid="signin-create-password"
                autoFocus
                rightAction={
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
                  >
                    {showPwd ? <EyeOff size={14} strokeWidth={1.6} /> : <Eye size={14} strokeWidth={1.6} />}
                  </button>
                }
              />
              <Field
                Icon={Lock}
                type={showPwd ? "text" : "password"}
                value={confirm}
                onChange={setConfirm}
                placeholder="Confirm password"
                testid="signin-create-confirm"
              />
              <label className="flex items-center gap-2 cursor-pointer select-none -mt-1">
                <input
                  type="checkbox"
                  data-testid="signin-create-remember"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 accent-[var(--ld-gold-deep)]"
                />
                <span className="text-[12px] text-[var(--ld-ink)]">
                  Keep me signed in for 30 days
                </span>
              </label>
              <button
                data-testid="signin-create-submit"
                onClick={doRegister}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3.5 hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Spinner /> : null}
                Create account
              </button>
              <button
                onClick={() => reset(STEP.EMAIL)}
                className="block mx-auto text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] pt-2"
              >
                ← Use a different email
              </button>
            </>
          )}

          {step === STEP.FORGOT && (
            <>
              <Field
                Icon={Mail}
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="your@email.com"
                testid="signin-forgot-email"
                autoFocus
              />
              <button
                data-testid="signin-forgot-submit"
                onClick={doForgot}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3.5 hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Spinner /> : null}
                Send reset link
              </button>
              <button
                onClick={() => reset(STEP.EMAIL)}
                className="block mx-auto text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] pt-2"
              >
                ← Back to sign in
              </button>
            </>
          )}

          {error && (
            <p
              data-testid="signin-error"
              className="text-[12px] text-rose-600 text-center pt-1"
            >
              {error}
            </p>
          )}
        </div>
      </section>

      <footer className="px-6 md:px-12 py-6 text-center text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
        © {new Date().getFullYear()} · Luxé Directory
      </footer>
    </main>
  );
};

export default SignIn;
